// vuex
import { mapState, mapMutations } from 'vuex'

// components
import maTabNav from '@/components/tabnav'
import maModal from '@/components/modal'
import maInputFile from '@/components/input/modules/file'
import maForm from '@/components/form'
import maInputSelect from '@/components/input/modules/select'
import maFilters from './components/filters'

// api
import {
  apiGetAdminGAGTemplates,
  apiPostAdminGAGTemplates
} from '@/services/API/adminGag'

// utils
import downloadBlobFileXML from '@/utils/downloadBlobFileXML'
import maEventbus from '@/components/eventbus'

// types
import {
  USER_MODULE_NAME,
  UPDATE_LOADER
} from '@/store/types'

/**
 * @module
 * @name adminGAGLayout
 */
export default {
  name: 'AdminGAGLayout',

  /**
   * @property {Array} dataUserRoutes Parsed routes, used to render navigation
   *  tabs.
   * @property {File} paramsFile File object used to store parameters import file.
   * @property {Boolean} loadingImport True if file upload (XHR request) is pending.
   */
  data: function () {
    return {
      dataUserRoutes: [],
      paramsFile: null,
      loadingImport: false
    }
  },
  components: {
    maTabNav,
    maModal,
    maInputFile,
    maForm,
    maInputSelect,
    maFilters
  },

  /**
   * @property {String} tabId Used to access to current route warranty group id.
   */
  computed: {
    ...mapState(USER_MODULE_NAME, {
      stateUserRoutes: 'routes',
      stateUserRole: 'role'
    }),
    tabId: function () {
      return this.$route.meta.id
    }
  },

  methods: {
    ...mapMutations({
      updateLoader: UPDATE_LOADER
    }),

    /**
     * @function
     * @name handleExport
     * @param {String} tabId Current route tab id.
     * @param {String} filterWarrantyId Filter warranty id.
     * @param {String} filterSubwarrantyId Filter subwarranty id.
     */
    handleExport: function (tabId) {
      this.updateLoader(true)

      apiGetAdminGAGTemplates(tabId)
        .then(download => {
          this.updateLoader(false)
          downloadBlobFileXML(download, 'MAVALUE_GAG_ALL.xlsx')
        })
        .catch(err => {
          this.throwNotification(this.$t('notifications.error.0.title'), this.$t('notifications.error.0.body'), 'KO')
          console.debug('err', err)
        })
        .finally(() => {
          this.updateLoader(false)
        })
    },

    /**
     * @function
     * @name handleExportClick
     * @description It shows modal with settings used to export tab info as file.
     */
    handleExportClick: function () {
      this.handleExport(this.tabId)
    },

    /**
     * @function
     * @name handleExportAllClick
     * @description Trigger handleExport with no filters.
     */
    handleExportAllClick: function () {
      this.handleExport()
    },

    /**
     * @function
     * @name handleExportCancel
     * @description Reset filter and hide modal.
     */
    handleExportCancel: function () {
      this.filter = null
      this.$refs.adminExportModal.showModal = false
    },

    /**
     * @function
     * @name handleImportClick
     * @description It shows import modal.
     */
    handleImportClick: function () {
      this.$refs.adminImportModal.showModal = true
    },

    /**
     * @function
     * @name handleImportCancel
     * @description It hides import modal.
     */
    handleImportCancel: function () {
      this.$refs.adminImportModal.showModal = false
    },

    /**
     * @function
     * @name handleImportConfirm
     * @description Handler for importing files. It posts file to backend.
     */
    handleImportConfirm: function () {
      this.loadingImport = true
      this.updateLoader(true)

      apiPostAdminGAGTemplates(this.paramsFile)
        .then(() => {
          this.$refs.adminImportModal.showModal = false
          this.throwNotification(this.$t('adminLayout.notifications.importedFile.title'), this.$t('adminLayout.notifications.importedFile.body'), 'OK')
        })
        .catch(err => {
          this.throwNotification(this.$t('notifications.error.0.title'), this.$t('notifications.error.0.body'), 'KO')
          console.debug('err', err)
        })
        .finally(() => {
          this.loadingImport = false
          this.updateLoader(false)
          maEventbus.$emit('onAdminGAGImportTemplateEnd')
        })
    },

    /**
     * @name handleImportInputChange
     * @param {File} file Target file
     * @description Set local state file to param file.
     */
    handleImportInputChange: function (file) {
      this.paramsFile = file
    },

    /**
     * @name handleImportInputDelete
     * @description Set local state file to null.
     */
    handleImportInputDelete: function () {
      this.paramsFile = null
    },

    /**
     * @name checkRoutes
     * @param {Array} routes Routes array.
     * @description Push route to dataUserRoutes if it belongs to current
     *  warranty id group.
     */
    checkRoutes: function (routes) {
      routes.forEach(route => {
        if (route.name === 'admin') {
          route.children.forEach(childRoute => {
            if (this.$route.fullPath.includes(childRoute.path)) {
              childRoute.children.forEach(c => {
                this.dataUserRoutes.push(c)
              })
            }
          })
        }
      })
    }
  },

  /**
   * @function
   * @name mounted
   * @description Init tab routes
   */
  mounted: function () {
    this.dataUserRoutes = []
    this.checkRoutes(this.stateUserRoutes)
  }
}
